<template>
  <div class="audio-player">
    <div class="audio-player__times">
      <span>{{ currentTimeFormatted }}</span>
      <span>{{ totalTimeFormatted }}</span>
    </div>
    <div class="audio-player__scrubber">
      <slider 
        class="audio-player__slider"
        :min="0" 
        :max="totalTime" 
        :step="0.01" 
        :modelValue="audioSeekSliderModel"
        @update:modelValue="(value) => onUpdateAudioSlider(value)"
        @change="onChangeAudioSlider()" 
        @keydown.prevent
      />
    </div>
  </div>
</template>
<script setup lang="ts">

import Slider from '@/components/common/Slider.vue'
import useAudioEngine from '@/composables/UseAudioEngine'
import timeFormat from '@/utils/time-format'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  currentTime: number
  totalTime: number
}>()

const currentTimeFormatted = computed(() => {
  return timeFormat(audioSeekSliderModel.value)
})

const totalTimeFormatted = computed(() => {
  return timeFormat(props.totalTime)
})

const { seekAudio } = useAudioEngine()

const audioSeekSliderModel = ref(0)
const sliderDragging = ref(false)

watch(() => props.currentTime, () => {
  if (!sliderDragging.value) {
    audioSeekSliderModel.value = props.currentTime
  }
})

const onUpdateAudioSlider = (value: number) => {
  sliderDragging.value = true
  audioSeekSliderModel.value = value
}

const onChangeAudioSlider = () => {
  sliderDragging.value = false
  seekAudio(audioSeekSliderModel.value)
}

</script>
<style lang="scss">
.audio-player { 
  margin-top: 31px; // To make sure this is positioned well inside the min-height of the preview-footer
  &__times {
    display: flex;
    justify-content: space-between;
    color: $grey-50;
    font-size: 12px;
  }
  
  &__scrubber {
    display: flex;
    align-items: center;
    height: 20px;
    margin-bottom: 6px;
  }

  &__slider {
    height: 2px;
    width: 100%;
    transition: height 250ms $animationCurve;
    
    &:focus { outline: none; }

    &::-webkit-slider-thumb {
      height: 12px;
      width: 12px;
      background-color: $purple;
      opacity: 0;
      transition: opacity 250ms $animationCurve;
    }
  }

  &:hover {
    .audio-player__slider {
      height: 4px;
  
      &::-webkit-slider-thumb {
        opacity: 1;
      }
    }
  }

}
</style>
